import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Package, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Search,
  Filter,
  ArrowLeft,
  ClipboardCheck,
} from 'lucide-react';
import { assetApi, api } from '@/utils/api';
import { canAddAssets, canManageAssetTypes, getStoredUser } from '@/utils/roleGate';
import type { Asset } from '@/types';
import { AssetDetailModal } from '@/components/AssetDetailModal';
import { AssetFormModal } from '@/components/AssetFormModal';
import { AssetTypeManagerModal } from '@/components/AssetTypeManagerModal';
import { InspectionModal } from '@/components/InspectionModal';

export function AssetsPage() {
  const [searchParams] = useSearchParams();
  const urlFilter = searchParams.get('filter');

  // Phase 2b (ROLE-MATRIX-v1): crew tier sees only gear currently assigned to
  // them and cannot add assets. The server scopes the LIST endpoint the same
  // way — this client filter is UX truth, not security. (Broader "crew
  // equipment" scope is an unresolved design question — NOT attempted here.)
  const storedUser = getStoredUser();
  const canAdd = canAddAssets(storedUser.role);
  // WD-T3 M3: same tier gate drives the detail modal's Edit/Delete — a worker
  // opens their assigned gear read-only (server write scope is foreman+ too).
  const canManage = canAdd;
  // S2-assets: custom type management is management+admin only (server-gated too)
  const canManageTypes = canManageAssetTypes(storedUser.role);

  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>(urlFilter || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [inspectingAsset, setInspectingAsset] = useState<Asset | null>(null);
  const [showTypeManager, setShowTypeManager] = useState(false);

  // Set filter from URL on mount
  useEffect(() => {
    if (urlFilter) {
      setFilter(urlFilter);
    }
  }, [urlFilter]);

  useEffect(() => {
    loadAssets();
  }, [filter]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      // WD-T2 (D): warehouse-day view — monthly-cadence gear due this month.
      // Server returns due rows (incl. overdue) for the MONTHLY cadence only;
      // NONE-cadence gear is never listed (no invented due dates). Rows are
      // mapped into the Asset-ish shape the table already renders.
      if (filter === 'due-monthly') {
        const response = await api.get('/assets/due-inspections', { params: { interval: 'MONTHLY' } });
        const rows = (response.data?.data ?? []) as any[];
        setAssets(rows.map((a) => ({
          ...a,
          daysUntilExpiration: null,
          expirationDate: null,
          calibrationDue: null,
          daysUntilCalibration: null,
          currentHolderId: a.currentHolder?.id ?? null,
          lastInspection: a.lastInspectionDate
            ? { date: a.lastInspectionDate, status: `monthly cadence` }
            : null,
        })));
        return;
      }
      const response = await assetApi.getAll(
        filter === 'expiring' ? { expiringWithinDays: 90 } : {}
      );
      setAssets(response.data);
    } catch (error) {
      console.error('Error loading assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return 'bg-green-100 text-green-800';
      case 'IN_USE':
        return 'bg-blue-100 text-blue-800';
      case 'DAMAGED':
        return 'bg-red-100 text-red-800';
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-800';
      case 'MAINTENANCE':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getExpirationWarning = (daysUntil: number | null) => {
    if (daysUntil === null) return null;
    if (daysUntil < 0) return { level: 'expired', text: 'EXPIRED', color: 'text-red-600' };
    if (daysUntil <= 30) return { level: 'critical', text: `${daysUntil} days`, color: 'text-red-600' };
    if (daysUntil <= 90) return { level: 'warning', text: `${daysUntil} days`, color: 'text-yellow-600' };
    return { level: 'ok', text: `${daysUntil} days`, color: 'text-green-600' };
  };

  const filteredAssets = assets
    // WD-T3: list scope (own gear + crew gear) is enforced SERVER-side via
    // buildAssetScope — the client renders what the API returns. Only the
    // presentation filters (search/status) apply here.
    .filter((asset) => {
    const matchesSearch = 
      asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (['damaged', 'DAMAGED', 'AVAILABLE', 'IN_USE', 'MAINTENANCE', 'EXPIRED'].includes(filter)) {
      return matchesSearch && asset.status === filter.toUpperCase();
    }
    
    // 'expiring' filter is handled by the API call, so just apply search
    return matchesSearch;
  });

  const handleAssetClick = (asset: Asset) => {
    console.log('Asset clicked:', asset.name);
    setSelectedAsset(asset);
    setShowDetailModal(true);
  };

  const handleAddAsset = () => {
    setEditingAsset(null);
    setShowFormModal(true);
  };

  const handleEditAsset = () => {
    setEditingAsset(selectedAsset);
    setShowDetailModal(false);
    setShowFormModal(true);
  };

  const handleDeleteAsset = async () => {
    if (!selectedAsset) return;
    
    if (confirm(`Are you sure you want to delete "${selectedAsset.name}"?`)) {
      try {
        await assetApi.delete(selectedAsset.id.toString());
        setShowDetailModal(false);
        setSelectedAsset(null);
        loadAssets(); // Reload the list
      } catch (error) {
        console.error('Error deleting asset:', error);
        alert('Failed to delete asset');
      }
    }
  };

  const handleSaveAsset = async (assetData: any) => {
    try {
      if (editingAsset) {
        // Update existing asset
        await assetApi.update(editingAsset.id.toString(), assetData);
      } else {
        // Create new asset
        await assetApi.create(assetData);
      }
      setShowFormModal(false);
      setEditingAsset(null);
      loadAssets(); // Reload the list
    } catch (error) {
      console.error('Error saving asset:', error);
      throw error; // Let the form handle the error
    }
  };

  const handleInspectAsset = (asset: Asset, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setInspectingAsset(asset);
    setShowInspectionModal(true);
  };

  const handleSaveInspection = async (inspectionData: any) => {
    try {
      const response = await assetApi.inspect(inspectionData.assetId, inspectionData.status, inspectionData.notes);
      if (inspectionData.status !== 'PASS' && inspectionData.damageDescription) {
        await assetApi.reportDamage(
          inspectionData.assetId,
          inspectionData.damageDescription,
          inspectionData.severity
        );
      }
      setShowInspectionModal(false);
      setInspectingAsset(null);
      loadAssets(); // Reload to show updated inspection
    } catch (error) {
      console.error('Error saving inspection:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading assets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-gray-600 hover:text-gray-900">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900">Asset Management</h1>
                <p className="text-sm text-gray-600">Track equipment lifecycle and inspections</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
            {canManageTypes && (
              <Link
                to="/locations"
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Locations
              </Link>
            )}
            {canManageTypes && (
              <button
                onClick={() => setShowTypeManager(true)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Manage Types
              </button>
            )}
            {canAdd && (
              <button
                onClick={handleAddAsset}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Add Asset
              </button>
            )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats — U3 (WD-T4): each card is a clickable filter wired to the
            SAME filter state as the status dropdown below. */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <button
            type="button"
            onClick={() => setFilter('all')}
            className="bg-white p-6 rounded-lg shadow text-left w-full hover:shadow-md hover:border-blue-300 border border-transparent transition"
          >
            <div className="flex items-center justify-between mb-2">
              <Package className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">{assets.length}</span>
            </div>
            <p className="text-sm text-gray-600">Total Assets</p>
          </button>

          <button
            type="button"
            onClick={() => setFilter('AVAILABLE')}
            className="bg-white p-6 rounded-lg shadow text-left w-full hover:shadow-md hover:border-green-300 border border-transparent transition"
          >
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <span className="text-2xl font-bold text-gray-900">
                {assets.filter((a) => a.status === 'AVAILABLE').length}
              </span>
            </div>
            <p className="text-sm text-gray-600">Available</p>
          </button>

          <button
            type="button"
            onClick={() => setFilter('expiring')}
            className="bg-white p-6 rounded-lg shadow text-left w-full hover:shadow-md hover:border-yellow-300 border border-transparent transition"
          >
            <div className="flex items-center justify-between mb-2">
              <Clock className="h-8 w-8 text-yellow-600" />
              <span className="text-2xl font-bold text-gray-900">
                {assets.filter((a) => a.daysUntilExpiration !== null && a.daysUntilExpiration <= 90).length}
              </span>
            </div>
            <p className="text-sm text-gray-600">Expiring Soon</p>
          </button>

          <button
            type="button"
            onClick={() => setFilter('DAMAGED')}
            className="bg-white p-6 rounded-lg shadow text-left w-full hover:shadow-md hover:border-red-300 border border-transparent transition"
          >
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="h-8 w-8 text-red-600" />
              <span className="text-2xl font-bold text-gray-900">
                {assets.filter((a) => a.status === 'DAMAGED').length}
              </span>
            </div>
            <p className="text-sm text-gray-600">Damaged</p>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="h-5 w-5 text-gray-600" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Assets</option>
                <option value="expiring">Expiring Soon</option>
                <option value="due-monthly">Monthly Inspection Due</option>
                <option value="AVAILABLE">Available</option>
                <option value="IN_USE">In Use</option>
                <option value="DAMAGED">Damaged</option>
              </select>
            </div>
          </div>
        </div>

        {/* Asset List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Asset
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Holder
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expiration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Inspection
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAssets.map((asset) => {
                const expirationWarning = getExpirationWarning(asset.daysUntilExpiration);
                const isDamagedOrExpired = asset.status === 'DAMAGED' || asset.status === 'EXPIRED';
                const isTestEquipment = asset.type === 'TEST_EQUIPMENT';
                
                return (
                  <tr 
                    key={asset.id} 
                    className="hover:bg-gray-50"
                  >
                    <td 
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => handleAssetClick(asset)}
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-900">{asset.name}</div>
                        <div className="text-sm text-gray-500">SN: {asset.serialNumber || 'N/A'}</div>
                      </div>
                    </td>
                    <td 
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => handleAssetClick(asset)}
                    >
                      <div className="text-sm text-gray-900">{asset.type.replace(/_/g, ' ')}</div>
                    </td>
                    <td 
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => handleAssetClick(asset)}
                    >
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(asset.status)}`}>
                        {asset.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td 
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                      onClick={() => handleAssetClick(asset)}
                    >
                      {asset.currentHolder ? (
                        `${asset.currentHolder.firstName} ${asset.currentHolder.lastName}`
                      ) : (
                        <span className="text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td 
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => handleAssetClick(asset)}
                    >
                      {expirationWarning ? (
                        <span className={`text-sm font-medium ${expirationWarning.color}`}>
                          {expirationWarning.text}
                        </span>
                      ) : asset.expirationDate === null && asset.calibrationDue !== null &&
                        (asset.daysUntilCalibration ?? 0) < 0 ? (
                        <span className="text-sm font-medium text-red-600">
                          Calibration overdue {asset.calibrationDue ? new Date(asset.calibrationDue).toLocaleDateString() : ''}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">No expiration</span>
                      )}
                    </td>
                    <td 
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                      onClick={() => handleAssetClick(asset)}
                    >
                      {asset.lastInspection ? (
                        <div>
                          <div>{new Date(asset.lastInspection.date).toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500">{asset.lastInspection.status}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">Never inspected</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        {/* Inspect/Calibrate Button */}
                        <button
                          onClick={(e) => handleInspectAsset(asset, e)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1 ${
                            isTestEquipment
                              ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                          title={isTestEquipment ? 'Calibration Check' : 'Inspect Asset'}
                        >
                          <ClipboardCheck className="h-3.5 w-3.5" />
                          {isTestEquipment ? 'Calibrate' : 'Inspect'}
                        </button>
                        
                        {/* View Details Button */}
                        <button
                          onClick={() => handleAssetClick(asset)}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-xs font-medium transition"
                          title="View Details"
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredAssets.length === 0 && (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              {filter === 'due-monthly' ? (
                <p className="text-gray-600">No monthly-cadence gear is due this month — nothing on the monthly list needs inspection right now</p>
              ) : (
                <p className="text-gray-600">No assets found</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showTypeManager && (
        <AssetTypeManagerModal onClose={() => setShowTypeManager(false)} />
      )}
      {showDetailModal && selectedAsset && (
        <AssetDetailModal
          asset={selectedAsset}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedAsset(null);
          }}
          onEdit={handleEditAsset}
          onDelete={handleDeleteAsset}
          canManage={canManage}
        />
      )}

      {showFormModal && (
        <AssetFormModal
          asset={editingAsset}
          onClose={() => {
            setShowFormModal(false);
            setEditingAsset(null);
          }}
          onSave={handleSaveAsset}
        />
      )}

      {showInspectionModal && inspectingAsset && (
        <InspectionModal
          assetId={inspectingAsset.id}
          assetName={inspectingAsset.name}
          assetType={inspectingAsset.type}
          onClose={() => {
            setShowInspectionModal(false);
            setInspectingAsset(null);
          }}
          onSave={handleSaveInspection}
        />
      )}

    </div>
  );
}

